import React from 'react';
import styled from 'styled-components';
import { ErrorDetail } from '../../contexts/ErrorContext';

// Styled components
const ErrorContainer = styled.div`
  margin: 15px 0;
  padding: 20px;
  border: 1px solid #e0a0a0;
  border-radius: 4px;
  background-color: #fff0f0;
  color: #d32f2f;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const ErrorTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  margin-bottom: 15px;
`;

const ErrorDetails = styled.details`
  margin-top: 12px;
  white-space: pre-wrap;
`;

const ErrorSummary = styled.summary`
  cursor: pointer;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #444;
`;

const ErrorStack = styled.pre`
  margin: 10px 0;
  padding: 12px;
  background-color: #f8f8f8;
  border-radius: 3px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.4;
  color: #333;
  max-height: 200px;
`;

const ResetButton = styled.button`
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  margin-top: 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #b71c1c;
  }
`;

// Props interface
interface ErrorFallbackProps {
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
  errorDetail?: ErrorDetail;
  resetErrorBoundary?: () => void;
  componentName?: string;
  title?: string;
  showResetButton?: boolean;
  children?: React.ReactNode;
}

/**
 * ErrorFallback Component
 * A reusable component to display error information
 * Can be used as a fallback UI for ErrorBoundary or standalone
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  errorDetail,
  resetErrorBoundary,
  componentName,
  title = 'Something went wrong',
  showResetButton = true,
  children,
}) => {
  // Use the most specific error information available
  const errorMessage =
    errorDetail?.message || error?.message || 'An unknown error occurred';
  const errorStack = errorDetail?.stack || error?.stack;
  const componentStack =
    errorInfo?.componentStack ||
    (errorDetail?.metadata?.componentStack as string);

  return (
    <ErrorContainer>
      <ErrorTitle>{title}</ErrorTitle>

      <ErrorMessage>
        {componentName && (
          <div>
            <strong>Component:</strong> {componentName}
          </div>
        )}
        <div>
          <strong>Error:</strong> {errorMessage}
        </div>
      </ErrorMessage>

      {children}

      {(errorStack || componentStack) && (
        <ErrorDetails>
          <ErrorSummary>Show technical details</ErrorSummary>
          {errorStack && (
            <>
              <div>
                <strong>Stack trace:</strong>
              </div>
              <ErrorStack>{errorStack}</ErrorStack>
            </>
          )}
          {componentStack && (
            <>
              <div>
                <strong>Component stack:</strong>
              </div>
              <ErrorStack>{componentStack}</ErrorStack>
            </>
          )}
        </ErrorDetails>
      )}

      {showResetButton && resetErrorBoundary && (
        <ResetButton onClick={resetErrorBoundary}>Try again</ResetButton>
      )}
    </ErrorContainer>
  );
};

export default ErrorFallback;
